/**
 * WebSocket event protocol: server -> browser.
 *
 * A single socket carries every live update. HTTP requests are always
 * short-lived (the proxy has a ~125s read timeout), so anything long-running
 * is started over HTTP and reported here.
 *
 * Every event carries a monotonic `seq` so a reconnecting client can ask to
 * resume and detect gaps.
 */

import type { BuildJob, BuildLogLine, ServeStatus } from './build.js';
import type { JobId } from './jobs.js';
import type { Task, TaskStatus } from './tasks.js';
import type { EditorRequestOp } from './game-trace.js';
import type { SubagentTag } from './ipc.js';

export type ApprovalRisk = 'low' | 'medium' | 'high';

export interface ModelCallSummary {
  readonly id: string;
  readonly provider: string;
  readonly model: string;
  readonly keySlot: number;
  readonly inputTokens: number | null;
  readonly outputTokens: number | null;
  readonly contextChars: number;
  readonly status: 'running' | 'completed' | 'failed';
}

export interface ModelCallTrace extends ModelCallSummary {
  readonly startedAt: string;
  readonly durationMs: number;
  /** Effective local replay, not the provider's hidden remote state. */
  readonly context: readonly {
    readonly role: string;
    readonly content: string;
    readonly toolCallId?: string;
    readonly toolCalls?: readonly {
      readonly id: string;
      readonly name: string;
      readonly arguments: Record<string, unknown>;
    }[];
  }[];
  readonly tools: readonly {
    readonly name: string;
    readonly description: string;
    readonly parameters: Record<string, unknown>;
  }[];
  /** Actual JSON request bodies, without headers or credential values. */
  readonly requests: readonly Record<string, unknown>[];
  readonly output: string;
  readonly error: string | null;
  readonly toolResults: readonly {
    readonly id: string;
    readonly name: string;
    readonly arguments: Record<string, unknown>;
    readonly output: string;
    readonly ok: boolean;
  }[];
}

export interface ApprovalRequest {
  readonly id: string;
  readonly projectId: number;
  readonly memberId: number;
  readonly memberName: string;
  readonly taskId: number | null;
  readonly tool: string;
  readonly risk: ApprovalRisk;
  readonly reason: string;
  /** Unified diff for file mutations, when one can be computed. */
  readonly diff: string | null;
  /** Shell argv or equivalent, when the request is a command. */
  readonly command: readonly string[] | null;
  readonly targetPath: string | null;
  readonly expiresAt: number;
}

/** A shared sound-cache download (`POST /api/sound/cache/download`). */
export interface SoundCacheJob {
  readonly id: string;
  readonly kind: 'soundfont' | 'voice' | 'dict';
  readonly assetId: string;
  readonly status: 'downloading' | 'verifying' | 'done' | 'error';
  /** Bytes received so far over all files of the asset. */
  readonly received: number;
  /** Expected total bytes, when known. */
  readonly total: number | null;
  readonly error: string | null;
  readonly startedAt: number;
  readonly finishedAt: number | null;
}

export type ServerEvent =
  | { readonly type: 'editor.play'; readonly seq: number; readonly projectId: number }
  | {
      /**
       * The server asks the live editor something only the running Play view
       * knows (world snapshot, one entity, trace config). Answered with a
       * `editor.response` client event carrying the same requestId.
       */
      readonly type: 'editor.request';
      readonly seq: number;
      readonly projectId: number;
      readonly requestId: string;
      readonly op: EditorRequestOp;
      readonly args: Record<string, unknown>;
    }
  | {
      readonly type: 'hello';
      readonly seq: number;
      readonly version: string;
      readonly serverTime: number;
    }
  | { readonly type: 'pong'; readonly seq: number; readonly serverTime: number }
  | {
      readonly type: 'agent.status';
      readonly seq: number;
      readonly memberId: number;
      readonly state: 'idle' | 'starting' | 'running' | 'waiting_approval' | 'stopped' | 'error';
      readonly taskId: number | null;
      readonly detail: string | null;
    }
  | {
      readonly type: 'agent.output';
      readonly seq: number;
      readonly memberId: number;
      readonly stream: 'assistant' | 'thinking' | 'note';
      readonly delta: string;
      readonly callId?: string;
      /** Set when one of the member's sub-agents produced this, not the member. */
      readonly agent?: SubagentTag;
    }
  | {
      readonly type: 'agent.message';
      readonly seq: number;
      readonly memberId: number;
      readonly stream: 'assistant' | 'thinking' | 'note';
      readonly text: string;
      readonly callId?: string;
      readonly agent?: SubagentTag;
    }
  | {
      readonly type: 'agent.tool';
      readonly seq: number;
      readonly memberId: number;
      readonly taskId: number | null;
      readonly phase: 'start' | 'end';
      readonly tool: string;
      readonly summary: string;
      readonly detail: string | null;
      readonly ok: boolean | null;
      readonly callId?: string;
      readonly toolCallId?: string;
      readonly arguments?: Record<string, unknown>;
      readonly agent?: SubagentTag;
    }
  | {
      readonly type: 'agent.call';
      readonly seq: number;
      readonly memberId: number;
      readonly call: ModelCallSummary;
      readonly agent?: SubagentTag;
    }
  | {
      /**
       * A sub-agent started by one of the member's tool calls (spawn_agents,
       * or a Claude Code workflow agent) changed state. Live only: never
       * persisted or replayed.
       */
      readonly type: 'agent.subagent';
      readonly seq: number;
      readonly projectId: number;
      readonly memberId: number;
      readonly parentToolCallId: string;
      /** The model call that issued the parent tool call (see SubagentTag). */
      readonly parentCallId?: string;
      readonly agentId: string;
      readonly label: string;
      readonly state: 'running' | 'done' | 'failed' | 'stopped';
      readonly summary?: string;
    }
  | { readonly type: 'approval.request'; readonly seq: number; readonly request: ApprovalRequest }
  | {
      readonly type: 'approval.resolved';
      readonly seq: number;
      readonly requestId: string;
      readonly decision: 'approved' | 'denied';
      readonly by: string;
    }
  | { readonly type: 'task.upsert'; readonly seq: number; readonly task: Task }
  | { readonly type: 'task.delete'; readonly seq: number; readonly taskId: number }
  | {
      readonly type: 'member.status';
      readonly seq: number;
      readonly memberId: number;
      readonly enabled: boolean;
      readonly jobs: readonly JobId[];
    }
  | {
      readonly type: 'error';
      readonly seq: number;
      readonly code: string;
      readonly message: string;
    }
  | {
      /** A file in the project changed on disk (editor save, agent edit, git...). */
      readonly type: 'file.changed';
      readonly seq: number;
      readonly projectId: number;
      /** Forward-slash path relative to the project root. */
      readonly path: string;
    }
  | {
      /** Progress of a shared sound-cache download (SoundFont / TTS voice). */
      readonly type: 'sound.cache.progress';
      readonly seq: number;
      readonly job: SoundCacheJob;
    }
  | {
      /** A build (`prism export`) started, progressed or finished (Build window). */
      readonly type: 'build.progress';
      readonly seq: number;
      readonly projectId: number;
      readonly job: BuildJob;
    }
  | {
      /** One output line of a build or of the editor's dedicated server. */
      readonly type: 'build.log';
      readonly seq: number;
      readonly projectId: number;
      readonly jobId: string | null;
      readonly entry: BuildLogLine;
    }
  | {
      /** The editor's dedicated server (`prism serve`) started or stopped. */
      readonly type: 'serve.status';
      readonly seq: number;
      readonly projectId: number;
      readonly serve: ServeStatus;
    }
  | {
      readonly type: 'resumed';
      readonly seq: number;
      readonly fromSeq: number;
      readonly missed: number;
    };

export type ServerEventType = ServerEvent['type'];

/** Browser -> server. */
export type ClientEvent =
  | { readonly type: 'ping'; readonly t: number }
  | { readonly type: 'subscribe'; readonly projectId: number | null; readonly since: number | null }
  | { readonly type: 'typing'; readonly projectId: number; readonly active: boolean }
  | {
      /** Answer to an `editor.request`. */
      readonly type: 'editor.response';
      readonly requestId: string;
      readonly ok: boolean;
      readonly result?: unknown;
      readonly error?: string;
    };

export interface TaskPatch {
  readonly title?: string;
  readonly description?: string;
  readonly job?: JobId;
  readonly priority?: number;
  readonly status?: TaskStatus;
  readonly assigneeId?: number | null;
  readonly assignedByMemberId?: number | null;
  readonly blocked?: boolean;
  readonly blockedReason?: string | null;
  readonly dependsOn?: readonly number[];
}

export function isServerEvent(value: unknown): value is ServerEvent {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { type?: unknown }).type === 'string' &&
    typeof (value as { seq?: unknown }).seq === 'number'
  );
}
